import React from 'react';
import { X, UserCircle, Building2, Mail, RefreshCw, Globe } from 'lucide-react';
import { TimezoneSelect } from '../ui/TimezoneSelect';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import { Portal } from '../ui/Portal';
import { useState } from 'react';
import { auth } from '../../lib/firebase';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

interface ProfilePopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ProfilePopup: React.FC<ProfilePopupProps> = ({ isOpen, onClose }) => {
  const { user, signOut, account, refreshAccess, loading } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState<boolean>(false);

  const validatePassword = (password: string): { isValid: boolean; error: string | null } => {
    if (password.length < 8) {
      return { isValid: false, error: t('profile.passwordMinLength') };
    }
    if (!/[A-Z]/.test(password)) {
      return { isValid: false, error: t('profile.passwordMustContainUppercase') };
    }
    if (!/[a-z]/.test(password)) {
      return { isValid: false, error: t('profile.passwordMustContainLowercase') };
    }
    if (!/\d/.test(password)) {
      return { isValid: false, error: t('profile.passwordMustContainNumber') };
    }
    return { isValid: true, error: null };
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentPassword) {
      setError(t('profile.currentPasswordRequired'));
      return;
    }

    const { isValid, error: validationError } = validatePassword(newPassword);
    if (!isValid) {
      setError(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('profile.passwordsMustMatch'));
      return;
    }

    if (currentPassword === newPassword) {
      setError(t('profile.passwordMustBeDifferent'));
      return;
    }

    try {
      if (!auth.currentUser?.email) return;

      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword
      );

      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      setShowSuccessBanner(true);
      setIsChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        setShowSuccessBanner(false);
      }, 5000);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string };
        if (firebaseError.code === 'auth/requires-recent-login') {
          setError(t('profile.requiresRecentLogin'));
        } else if (firebaseError.code === 'auth/wrong-password') {
          setError(t('profile.wrongPassword'));
        } else {
          setError(t('profile.passwordError'));
        }
      } else if (error && typeof error === 'object' && 'message' in error && error.message === 'New password should be different from the old password') {
        setError(t('profile.passwordMustBeDifferent'));
      } else {
        setError(t('profile.passwordError'));
      }
      console.error('Error updating password:', error);
    }
  };

  if (!isOpen) return null;

  const handleClickOutside = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        onClick={handleClickOutside}
      >
        <div className="bg-white shadow-xl w-full max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-10 py-8 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">{t('profile.title')}</h2>
            {showSuccessBanner && (
              <div className="absolute top-4 right-4 bg-white border border-green-200 text-green-800 px-4 py-2 text-sm shadow-sm">
                {t('profile.passwordUpdated')}
              </div>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 p-1 hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-10 py-8 space-y-10">
            {/* User Info */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-[#0055FF] flex items-center justify-center">
                  <UserCircle className="w-10 h-10 text-white" />
                </div>
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {user?.email}
                  </div>
                  <div className="text-sm text-gray-600 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    {account}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsChangingPassword(!isChangingPassword)}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {t('profile.changePassword')}
              </button>

              {isChangingPassword && (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  {error && (
                    <div className="text-sm text-red-700 bg-red-100 border border-red-400 px-3 py-2 flex items-center justify-between">
                      <span>{error}</span>
                      <button
                        type="button"
                        onClick={() => setError(null)}
                        className="ml-2 text-red-500 hover:text-red-700 font-bold text-lg leading-none"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  {success && (
                    <div className="text-sm text-green-700 bg-green-100 border border-green-400 px-3 py-2 flex items-center justify-between">
                      <span>{success}</span>
                      <button
                        type="button"
                        onClick={() => setSuccess(null)}
                        className="ml-2 text-green-500 hover:text-green-700 font-bold text-lg leading-none"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile.currentPassword')}
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 shadow-sm text-sm focus:ring-[#0055FF] focus:border-[#0055FF]"
                      required
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    {t('profile.passwordRequirements')}
                    <ul className="list-disc ml-4 mt-1 space-y-0.5">
                      <li>{t('profile.requirement8Chars')}</li>
                      <li>{t('profile.requirementUpperLower')}</li>
                      <li>{t('profile.requirementNumber')}</li>
                    </ul>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile.newPassword')}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 shadow-sm text-sm focus:ring-[#0055FF] focus:border-[#0055FF]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile.confirmPassword')}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 shadow-sm text-sm focus:ring-[#0055FF] focus:border-[#0055FF]"
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsChangingPassword(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-[#0055FF] hover:bg-[#0044CC] transition-colors"
                    >
                      {t('profile.updatePassword')}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Language */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                {t('profile.language')}
              </label>
              <select
                value={language}
                onChange={(e) => changeLanguage(e.target.value as 'es' | 'en')}
                className="w-full text-sm border border-gray-300 px-3 py-2 pr-12 bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-[#0055FF] focus:border-[#0055FF] appearance-none"
                style={{ backgroundImage: 'url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2Ij48cGF0aCBmaWxsPSIjNjY2IiBkPSJNNC4yOTQgNi41NzVsNC41NzUgNC41NzUgNC41NzUtNC41NzUtMS40MTQtMS40MTRMOC44NTkgOC4xNDUgNS43MDggNS4xMzNsLTEuNDE0IDEuNDEzeiIvPjwvc3ZnPg==)', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '1rem' }}
              >
                <option value="es">{t('languages.es')}</option>
                <option value="en">{t('languages.en')}</option>
              </select>
            </div>

            {/* Timezone */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                {t('profile.timezone')}
              </label>
              <TimezoneSelect />
            </div>
          </div>

          {/* Footer */}
          <div className="px-10 py-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={signOut}
                className="text-sm text-red-600 hover:text-red-800 transition-colors"
              >
                {t('navigation.logout')}
              </button>
              <button
                onClick={refreshAccess}
                disabled={loading}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                {t('profile.updateAccess')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};
